/**
 * EquityCurve — area chart of total equity over the last N days.
 *
 * Uses recharts because Phase 4 needs a single chart and recharts is
 * already lightweight + SVG-native. Tailwind tokens are read at
 * component-render time (CSS vars come from globals.css).
 */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Area, AreaChart, CartesianGrid, ReferenceArea, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import type { EquityPoint } from '@/hooks/useDashboardApi'
import { useEvents } from '@/hooks/useDashboardApi'
import { EVENT_TONE } from '@/pages/app/Events'
import { formatUsd } from '@/lib/format'

interface EquityCurveProps {
  data: EquityPoint[] | undefined
  days: number
  loading?: boolean
}

export function EquityCurve({ data, days, loading }: EquityCurveProps) {
  const { t } = useTranslation('app')

  // PULSE-228: возраст данных. Хартбит может умереть молча (инцидент
  // 10.07: 19ч протухших значений) — плашка честно говорит об этом.
  // Метка бакета = его НАЧАЛО: на 7-дневном виде (6ч-бакеты) последняя
  // точка легально «отстаёт» до 6ч — тревожим только сверх размера бакета.
  const staleMin = useMemo(() => {
    if (!data || !data.length) return null
    const last = new Date(data[data.length - 1].bucket).getTime()
    const bucketMin = days <= 2 ? 60 : days <= 14 ? 360 : 1440
    const age = Math.round((Date.now() - last) / 60000)
    return age - bucketMin
  }, [data, days])

  const prepared = useMemo(
    () => (data ?? []).map((p) => ({
      bucket: new Date(p.bucket).getTime(),
      label: formatBucketLabel(new Date(p.bucket), days),
      total_equity: p.total_equity,
      realized_pnl: p.realized_pnl,
      unrealized_pnl: p.unrealized_pnl,
    })),
    [data, days],
  )

  // PULSE-42 annotations overlay: fetch events that fall inside the
  // chart's time window. Each event maps to either a vertical line
  // (point event) or a translucent band (period event).
  const fromIso = useMemo(
    () => new Date(Date.now() - days * 86400_000).toISOString(),
    [days],
  )
  const { data: eventsData } = useEvents({ from: fromIso, limit: 100 })
  const overlay = useMemo(() => {
    if (!prepared.length || !eventsData) return [] as Array<
      { id: number; type: string; title: string; xStart: string; xEnd?: string }
    >
    // Match event ts to the nearest bucket label so ReferenceLine has
    // an `x` value recharts can place. Outside-window events are dropped.
    const buckets = prepared.map((p) => p.bucket)
    const firstTs = buckets[0]
    const lastTs = buckets[buckets.length - 1]
    const nearestLabel = (ts: number) => {
      if (ts < firstTs || ts > lastTs) return null
      let best = prepared[0]
      let bestDiff = Math.abs(best.bucket - ts)
      for (const p of prepared) {
        const d = Math.abs(p.bucket - ts)
        if (d < bestDiff) { best = p; bestDiff = d }
      }
      return best.label
    }
    return eventsData.items.flatMap((ev) => {
      const startLabel = nearestLabel(new Date(ev.ts_start).getTime())
      if (!startLabel) return []
      const endLabel = ev.ts_end ? nearestLabel(new Date(ev.ts_end).getTime()) : undefined
      return [{
        id: ev.id, type: ev.type, title: ev.title,
        xStart: startLabel, xEnd: endLabel,
      }]
    })
  }, [eventsData, prepared])

  if (loading) {
    return (
      <ChartFrame title={t('dashboard.equity.title')} subtitle={t('dashboard.equity.subtitle_days', { days })}>
        <div className="h-[220px] grid place-items-center text-textFaint text-sm">
          —
        </div>
      </ChartFrame>
    )
  }
  if (prepared.length === 0) {
    return (
      <ChartFrame title={t('dashboard.equity.title')} subtitle={t('dashboard.equity.subtitle_days', { days })}>
        <div className="h-[220px] grid place-items-center text-textFaint text-sm">
          {t('dashboard.equity.empty')}
        </div>
      </ChartFrame>
    )
  }

  return (
    <ChartFrame
      title={t('dashboard.equity.title')}
      subtitle={t('dashboard.equity.subtitle_days', { days })}
    >
      {staleMin !== null && staleMin > 15 && (
        <div className="mb-2 px-3 py-1.5 text-xs bg-amber-500/15 text-amber-400 rounded-sm font-mono">
          ⚠ данные устарели: хартбит отстаёт на ~{staleMin} мин — проверь депо-хартбит
        </div>
      )}
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={prepared} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="equity-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="var(--pulse)" stopOpacity={0.35} />
                <stop offset="100%" stopColor="var(--pulse)" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: 'var(--text-faint)', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              axisLine={{ stroke: 'var(--line)' }}
              tickLine={false}
              minTickGap={32}
            />
            <YAxis
              dataKey="total_equity"
              tick={{ fill: 'var(--text-faint)', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              axisLine={{ stroke: 'var(--line)' }}
              tickLine={false}
              tickFormatter={(v: number) => formatUsd(v, { compact: true })}
              width={56}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--elevated)',
                border: '1px solid var(--line)',
                borderRadius: 4,
                fontSize: 12,
              }}
              labelStyle={{ color: 'var(--text-faint)' }}
              itemStyle={{ color: 'var(--text)' }}
              formatter={(v) =>
                typeof v === 'number' ? formatUsd(v, { sign: true }) : String(v ?? '—')
              }
            />
            <Area
              type="monotone"
              dataKey="total_equity"
              name={t('dashboard.equity.legend') as string}
              stroke="var(--pulse)"
              strokeWidth={2}
              fill="url(#equity-grad)"
              dot={false}
              isAnimationActive={false}
            />

            {/* PULSE-42 annotations overlay */}
            {overlay.map((ann) => {
              const tone = (EVENT_TONE as Record<string, { line: string }>)[ann.type]
              const color = tone?.line ?? 'var(--text-faint)'
              if (ann.xEnd && ann.xEnd !== ann.xStart) {
                return (
                  <ReferenceArea
                    key={`a-${ann.id}`}
                    x1={ann.xStart}
                    x2={ann.xEnd}
                    fill={color}
                    fillOpacity={0.08}
                    stroke={color}
                    strokeOpacity={0.4}
                    label={undefined}
                    ifOverflow="hidden"
                  />
                )
              }
              return (
                <ReferenceLine
                  key={`l-${ann.id}`}
                  x={ann.xStart}
                  stroke={color}
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  ifOverflow="hidden"
                  label={{
                    value: ann.title.length > 24
                      ? ann.title.slice(0, 22) + '…' : ann.title,
                    position: 'top',
                    fill: color,
                    fontSize: 9,
                    fontFamily: 'JetBrains Mono',
                  }}
                />
              )
            })}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  )
}

function ChartFrame({
  title, subtitle, children,
}: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <section className="bg-ink p-5">
      <header className="flex items-baseline mb-4">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="ml-auto text-xs text-textFaint font-mono">{subtitle}</span>
      </header>
      {children}
    </section>
  )
}

/* ─── helpers ─────────────────────────────────────────────────────── */

function formatBucketLabel(d: Date, days: number): string {
  // 1-2d: HH:MM; 3-14d: Mon DD HH:00; >14d: Mon DD
  if (days <= 2) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  if (days <= 14) {
    return d.toLocaleString([], {
      month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  }
  return d.toLocaleDateString([], { month: 'short', day: '2-digit' })
}
